import { useState, useEffect } from 'react';
import { captureError } from '@/lib/logger';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, Trash2, TrendingUp, TrendingDown, Clock, AlertTriangle, Unlink, Undo2, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { fieldsToTransactionAt, transactionAtToFields } from './transactionAtFields';
import {
    deleteFailureMessage,
    deleteHeadline,
    groupLinkedRecords,
    LINKED_RECORDS_UNREAD,
    type LinkedRecord,
    type LinkedRecordEffect,
} from './linkedRecordEffects';

type TransactionType = 'CREDIT' | 'DEBIT';
type Currency = 'GHS' | 'USD';

interface FormState {
    amount: string;
    fee: string;
    date: string;
    time: string;
    type: TransactionType;
    currency: Currency;
    description: string;
    reference: string;
}

const INITIAL_STATE: FormState = {
    amount: '',
    fee: '',
    date: '',
    time: '',
    type: 'CREDIT',
    currency: 'GHS',
    description: '',
    reference: '',
};

interface AddTransactionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    transactionId?: number | null;
}

/** Returns true if the transaction was created ≤2 hours ago */
function isEditable(createdAt: string): boolean {
    const created = new Date(createdAt);
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
    return created >= twoHoursAgo;
}

/** How each group in the delete dialog looks: red only where a record is deleted or stops the delete */
const EFFECT_LOOK: Record<LinkedRecordEffect, { icon: LucideIcon; tone: string; box: string }> = {
    blocks: { icon: AlertTriangle, tone: 'text-destructive', box: 'bg-destructive/5' },
    deleted: { icon: Trash2, tone: 'text-destructive', box: 'bg-destructive/5' },
    unlinked: { icon: Unlink, tone: 'text-muted-foreground', box: 'bg-muted/30' },
    unassigned: { icon: Undo2, tone: 'text-muted-foreground', box: 'bg-muted/30' },
    unknown: { icon: AlertTriangle, tone: 'text-muted-foreground', box: 'bg-muted/30' },
};

export default function AddTransactionSheet({
    open,
    onOpenChange,
    transactionId,
}: AddTransactionSheetProps) {
    const queryClient = useQueryClient();
    const isEditMode = !!transactionId;
    const [state, setState] = useState<FormState>(INITIAL_STATE);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Fetch linked records when delete dialog opens
    const { data: linkedRecords, isLoading: linkedLoading, isError: linkedFailed } = useQuery({
        queryKey: ['transaction-linked-records', transactionId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_transaction_linked_records', {
                p_transaction_id: transactionId!,
            });
            if (error) throw error;
            return (data || []) as unknown as LinkedRecord[];
        },
        enabled: showDeleteConfirm && !!transactionId,
    });

    const updateState = (updates: Partial<FormState>) => {
        setState(prev => ({ ...prev, ...updates }));
    };

    // Fetch existing transaction for edit mode
    const { data: existingData } = useQuery({
        queryKey: ['transaction-edit', transactionId],
        queryFn: async () => {
            if (!transactionId) return null;
            const { data, error } = await supabase
                .from('transactions')
                .select('*')
                .eq('id', transactionId)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: open && isEditMode,
    });

    // Populate form when editing
    useEffect(() => {
        if (open && existingData && isEditMode) {
            const txFields = transactionAtToFields(existingData.transaction_at);
            setState({
                amount: existingData.amount?.toString() || '',
                fee: existingData.fee?.toString() || '',
                date: txFields.date,
                time: txFields.time,
                type: (existingData.type as TransactionType) || 'CREDIT',
                currency: (existingData.currency as Currency) || 'GHS',
                description: existingData.description || '',
                reference: existingData.reference || '',
            });
        }
    }, [existingData, isEditMode, open]);

    // Reset form when closed
    useEffect(() => {
        if (!open) {
            setState(INITIAL_STATE);
            setShowCloseConfirm(false);
            setShowDeleteConfirm(false);
        }
    }, [open]);

    const canSubmit = state.amount !== '' && parseFloat(state.amount) > 0 && state.date !== '' && state.time !== '';

    const isDirty = () => {
        if (isEditMode && existingData) {
            // Same reading of the stored instant the prefill used, or an untouched sheet would look
            // dirty from every browser whose zone disagrees with Accra.
            const txFields = transactionAtToFields(existingData.transaction_at);
            return (
                state.amount !== (existingData.amount?.toString() || '') ||
                state.fee !== (existingData.fee?.toString() || '') ||
                state.date !== txFields.date ||
                state.time !== txFields.time ||
                state.type !== ((existingData.type as TransactionType) || 'CREDIT') ||
                state.currency !== ((existingData.currency as Currency) || 'GHS') ||
                state.description !== (existingData.description || '') ||
                state.reference !== (existingData.reference || '')
            );
        }
        return state.amount !== '' || state.fee !== '' || state.date !== '' || state.time !== '' || state.description !== '' || state.reference !== '' || state.type !== 'CREDIT' || state.currency !== 'GHS';
    };

    const handleClose = () => {
        if (isDirty()) {
            setShowCloseConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    const resetAndClose = () => {
        setState(INITIAL_STATE);
        setShowCloseConfirm(false);
        setShowDeleteConfirm(false);
        onOpenChange(false);
    };

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['transactions-count'] });
        queryClient.invalidateQueries({ queryKey: ['transactions-monthly'] });
        queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
    };

    // Save mutation (insert or update)
    const mutation = useMutation({
        mutationFn: async () => {
            // Combine date + time into a full timestamp, read as GHANA wall clock — the same parts
            // the prefill showed, so an untouched edit writes back the instant it read.
            const transactionAt = fieldsToTransactionAt(state.date, state.time);
            if (!transactionAt) throw new Error('Enter a valid date and time for this transaction.');

            const payload = {
                amount: parseFloat(state.amount),
                fee: state.fee ? parseFloat(state.fee) : null,
                transaction_at: transactionAt,
                type: state.type,
                currency: state.currency,
                description: state.description.trim() || null,
                reference: state.reference.trim() || null,
            };

            if (isEditMode && transactionId) {
                const { error } = await supabase
                    .from('transactions')
                    .update(payload)
                    .eq('id', transactionId);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('transactions')
                    .insert(payload);
                if (error) throw error;
            }
        },
        onSuccess: () => {
            toast.success(isEditMode ? 'Transaction updated' : 'Transaction added');
            invalidateQueries();
            resetAndClose();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to save transaction');
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!transactionId) throw new Error('No transaction to delete');
            const { error } = await supabase
                .from('transactions')
                .delete()
                .eq('id', transactionId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Transaction deleted');
            invalidateQueries();
            resetAndClose();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(deleteFailureMessage(error));
        },
    });

    const formatCurrency = (amount: number, currency: Currency) =>
        new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount);

    const TypeIcon = state.type === 'CREDIT' ? TrendingUp : TrendingDown;

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[30vw] !min-w-[400px] !max-w-[500px] rounded-2xl flex flex-col shadow-2xl border bg-background overflow-hidden"
                >
                    {/* Header */}
                    <SheetHeader className="p-6 pb-4 border-b">
                        <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                state.type === 'CREDIT'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                                <TypeIcon className="h-5 w-5" />
                            </div>
                            <div>
                                <SheetTitle>
                                    {isEditMode ? 'Edit Transaction' : 'Add Transaction'}
                                </SheetTitle>
                                <SheetDescription className="text-xs">
                                    {isEditMode
                                        ? 'Update the details of this transaction.'
                                        : 'Record a new financial transaction.'}
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>

                    {/* Form Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                        {/* Amount + Fee row */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Amount *
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={state.amount}
                                    onChange={(e) => updateState({ amount: e.target.value })}
                                    placeholder="0.00"
                                    className="h-11"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Fee
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={state.fee}
                                    onChange={(e) => updateState({ fee: e.target.value })}
                                    placeholder="0.00"
                                    className="h-11"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Currency *
                                </Label>
                                <Select value={state.currency} onValueChange={(v) => updateState({ currency: v as Currency })}>
                                    <SelectTrigger className="h-11">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="GHS">GHS</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Type */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Type *
                            </Label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => updateState({ type: 'CREDIT' })}
                                    className={`flex items-center justify-center gap-2 h-11 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                                        state.type === 'CREDIT'
                                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-600'
                                            : 'border-border hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
                                    }`}
                                >
                                    <TrendingUp className="h-4 w-4" />
                                    Credit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateState({ type: 'DEBIT' })}
                                    className={`flex items-center justify-center gap-2 h-11 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                                        state.type === 'DEBIT'
                                            ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 dark:border-red-600'
                                            : 'border-border hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-900/10'
                                    }`}
                                >
                                    <TrendingDown className="h-4 w-4" />
                                    Debit
                                </button>
                            </div>
                        </div>

                        {/* Date & Time */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2 space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Date *
                                </Label>
                                <DatePicker
                                    // `state.date` is an Accra calendar day. `new Date('2026-08-10')`
                                    // is UTC midnight, which the picker reads with local getters and
                                    // highlights a day early at negative offsets — then writes that
                                    // day back on the next click. Parse to local midnight, the exact
                                    // inverse of the `format(date, 'yyyy-MM-dd')` below.
                                    date={state.date ? parse(state.date, 'yyyy-MM-dd', new Date()) : undefined}
                                    setDate={(date) => updateState({ date: date ? format(date, 'yyyy-MM-dd') : '' })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Time *
                                </Label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="time"
                                        value={state.time}
                                        onChange={(e) => updateState({ time: e.target.value })}
                                        className="pl-9 h-12"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Description
                            </Label>
                            <Textarea
                                value={state.description}
                                onChange={(e) => updateState({ description: e.target.value })}
                                placeholder="Enter a description for this transaction..."
                                rows={3}
                            />
                        </div>

                        {/* Reference */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Reference
                            </Label>
                            <Input
                                value={state.reference}
                                onChange={(e) => updateState({ reference: e.target.value })}
                                placeholder="Bank reference number"
                            />
                        </div>

                        {/* Preview card when amount is entered */}
                        {state.amount && parseFloat(state.amount) > 0 && (
                            <div className="bg-muted/30 border rounded-xl p-4 space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Preview</p>
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <span className={`text-2xl font-bold tracking-tight ${
                                            state.type === 'DEBIT' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                                        }`}>
                                            {state.type === 'DEBIT' ? '-' : '+'}{formatCurrency(parseFloat(state.amount), state.currency)}
                                        </span>
                                        {state.fee && parseFloat(state.fee) > 0 && (
                                            <span className="text-sm text-orange-600 ml-2">
                                                + {formatCurrency(parseFloat(state.fee), state.currency)} fee
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {state.date ? format(parse(state.date, 'yyyy-MM-dd', new Date()), 'PPP') : 'No date'}
                                        {state.time ? ` at ${state.time}` : ''}
                                    </span>
                                </div>
                                {state.description && (
                                    <p className="text-xs text-muted-foreground truncate">{state.description}</p>
                                )}
                                {state.reference && (
                                    <p className="text-[10px] text-muted-foreground/60 font-mono">Ref: {state.reference}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 pt-4 border-t bg-background/80 backdrop-blur-md space-y-3">
                        <div className="flex items-center gap-2">
                            {isEditMode && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={deleteMutation.isPending || mutation.isPending}
                                >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete
                                </Button>
                            )}
                            <div className="flex-1" />
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                disabled={mutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => mutation.mutate()}
                                disabled={!canSubmit || mutation.isPending}
                                className={state.type === 'CREDIT'
                                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg hover:shadow-emerald-500/25'
                                    : 'bg-red-600 hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                                }
                            >
                                {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {isEditMode ? 'Save Changes' : 'Add Transaction'}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Close Confirmation Dialog */}
            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p>{deleteHeadline(linkedFailed ? undefined : linkedRecords)}</p>
                                {linkedLoading && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Checking linked records…
                                    </div>
                                )}
                                {linkedFailed && <p>{LINKED_RECORDS_UNREAD}</p>}
                                {!linkedFailed && linkedRecords && groupLinkedRecords(linkedRecords).map(group => {
                                    const { icon: Icon, tone, box } = EFFECT_LOOK[group.effect];
                                    return (
                                        <div key={group.effect} className="space-y-1.5">
                                            <p className="text-xs font-medium text-foreground">{group.heading}</p>
                                            <div className={`rounded-lg border ${box} p-3 space-y-1.5`}>
                                                {group.records.map((r, i) => (
                                                    <div key={i} className="flex items-start gap-2 text-sm">
                                                        <Icon className={`h-3.5 w-3.5 ${tone} shrink-0 mt-1.5`} />
                                                        <Badge variant="outline" className="text-[10px] shrink-0">{r.label}</Badge>
                                                        {r.context && <span className="text-muted-foreground break-words text-xs mt-[5px]">{r.context}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
